/**
 * Thin, readable wrappers over the generated client.
 *
 * The generated names are derived from paths (`postApiV1SnippetsByIdTags`), which is fine for a
 * machine and poor for call sites. Everything the app uses goes through here, so a change to the
 * generator's naming is a change to one file.
 */
import './client';

import {
  deleteApiV1AuthAccount,
  deleteApiV1BillingTeamsByIdSubscription,
  getApiV1BillingPlan,
  getApiV1BillingTeamsById,
  postApiV1BillingTeamsByIdCheckout,
  postApiV1BillingTeamsByIdPortal,
  deleteApiV1SnippetsById,
  deleteApiV1SnippetsByIdFragmentsByFragmentId,
  deleteApiV1SnippetsByIdPermanent,
  deleteApiV1SnippetsByIdInvitationsByInvitationId,
  deleteApiV1SnippetsByIdSharesByUserId,
  deleteApiV1SnippetsByIdSharesMe,
  deleteApiV1SnippetsByIdTagsByTagId,
  deleteApiV1TagsById,
  deleteApiV1TeamsById,
  deleteApiV1TeamsByIdInvitationsByInvitationId,
  deleteApiV1TeamsByIdMembersByUserId,
  getApiV1Invitations,
  getApiV1SnippetsByIdInvitations,
  getApiV1SnippetsByIdShares,
  getApiV1Teams,
  getApiV1TeamsById,
  getApiV1TeamsByIdInvitations,
  postApiV1InvitationsByIdAccept,
  postApiV1InvitationsByIdDecline,
  postApiV1SnippetsByIdInvitationsByInvitationIdResend,
  postApiV1SnippetsByIdShares,
  postApiV1Teams,
  postApiV1TeamsByIdInvitations,
  postApiV1TeamsByIdInvitationsByInvitationIdResend,
  putApiV1TeamsById,
  putApiV1TeamsByIdMembersByUserId,
  getApiV1AuthMe,
  getApiV1Snippets,
  getApiV1SnippetsById,
  getApiV1Tags,
  postApiV1AuthChangePassword,
  postApiV1AuthConfirmEmail,
  postApiV1AuthForgotPassword,
  postApiV1AuthLogin,
  postApiV1AuthLogout,
  postApiV1AuthRegister,
  postApiV1AuthResendVerification,
  postApiV1AuthResetPassword,
  postApiV1Snippets,
  postApiV1SnippetsByIdFragments,
  postApiV1SnippetsByIdRestore,
  postApiV1SnippetsByIdTags,
  postApiV1Tags,
  putApiV1SnippetsById,
  putApiV1SnippetsByIdArchive,
  putApiV1SnippetsByIdDocument,
  putApiV1SnippetsByIdFavorite,
  putApiV1SnippetsByIdFragmentsByFragmentId,
  putApiV1SnippetsByIdFragmentsOrder,
  putApiV1TagsById,
} from './generated';

export const auth = {
  me: getApiV1AuthMe,
  register: postApiV1AuthRegister,
  login: postApiV1AuthLogin,
  logout: postApiV1AuthLogout,
  confirmEmail: postApiV1AuthConfirmEmail,
  resendVerification: postApiV1AuthResendVerification,
  forgotPassword: postApiV1AuthForgotPassword,
  resetPassword: postApiV1AuthResetPassword,
  changePassword: postApiV1AuthChangePassword,
  deleteAccount: deleteApiV1AuthAccount,
};

export const snippets = {
  list: getApiV1Snippets,
  get: getApiV1SnippetsById,
  create: postApiV1Snippets,
  update: putApiV1SnippetsById,
  setFavorite: putApiV1SnippetsByIdFavorite,
  setArchived: putApiV1SnippetsByIdArchive,
  remove: deleteApiV1SnippetsById,
  purge: deleteApiV1SnippetsByIdPermanent,
  restore: postApiV1SnippetsByIdRestore,
  addFragment: postApiV1SnippetsByIdFragments,
  updateFragment: putApiV1SnippetsByIdFragmentsByFragmentId,
  removeFragment: deleteApiV1SnippetsByIdFragmentsByFragmentId,
  reorderFragments: putApiV1SnippetsByIdFragmentsOrder,
  attachTag: postApiV1SnippetsByIdTags,
  detachTag: deleteApiV1SnippetsByIdTagsByTagId,
  listShares: getApiV1SnippetsByIdShares,
  // Creates an invitation, not a share. The recipient decides.
  share: postApiV1SnippetsByIdShares,
  unshare: deleteApiV1SnippetsByIdSharesByUserId,
  leaveShare: deleteApiV1SnippetsByIdSharesMe,
  listInvitations: getApiV1SnippetsByIdInvitations,
  resendInvitation: postApiV1SnippetsByIdInvitationsByInvitationIdResend,
  revokeInvitation: deleteApiV1SnippetsByIdInvitationsByInvitationId,
  saveDocument: putApiV1SnippetsByIdDocument,
};

/**
 * Diagrams, over the same endpoints.
 *
 * A diagram is a snippet row with `kind: 'Diagram'` and a canvas instead of fragments, so there is
 * one API group and one access rule. This alias is what stops the diagrams UI reading as though it
 * were operating on snippets, which is exactly what this file is for.
 */
export const diagrams = {
  list: getApiV1Snippets,
  get: getApiV1SnippetsById,
  create: postApiV1Snippets,
  update: putApiV1SnippetsById,
  saveDocument: putApiV1SnippetsByIdDocument,
  setFavorite: putApiV1SnippetsByIdFavorite,
  setArchived: putApiV1SnippetsByIdArchive,
  remove: deleteApiV1SnippetsById,
  purge: deleteApiV1SnippetsByIdPermanent,
  restore: postApiV1SnippetsByIdRestore,
  attachTag: postApiV1SnippetsByIdTags,
  detachTag: deleteApiV1SnippetsByIdTagsByTagId,
};

export const teams = {
  list: getApiV1Teams,
  get: getApiV1TeamsById,
  create: postApiV1Teams,
  update: putApiV1TeamsById,
  remove: deleteApiV1TeamsById,
  // There is no addMember. Membership only ever comes from somebody accepting an invitation.
  invite: postApiV1TeamsByIdInvitations,
  listInvitations: getApiV1TeamsByIdInvitations,
  resendInvitation: postApiV1TeamsByIdInvitationsByInvitationIdResend,
  revokeInvitation: deleteApiV1TeamsByIdInvitationsByInvitationId,
  changeRole: putApiV1TeamsByIdMembersByUserId,
  removeMember: deleteApiV1TeamsByIdMembersByUserId,
};

/** The invitee's side: one list and one decision per row, whichever kind it is. */
export const invitations = {
  mine: getApiV1Invitations,
  accept: postApiV1InvitationsByIdAccept,
  decline: postApiV1InvitationsByIdDecline,
};

export const billing = {
  plan: getApiV1BillingPlan,
  forTeam: getApiV1BillingTeamsById,
  checkout: postApiV1BillingTeamsByIdCheckout,
  portal: postApiV1BillingTeamsByIdPortal,
  cancel: deleteApiV1BillingTeamsByIdSubscription,
};

export const tags = {
  list: getApiV1Tags,
  create: postApiV1Tags,
  update: putApiV1TagsById,
  remove: deleteApiV1TagsById,
};

export { ensureAntiforgeryToken } from './client';
export type * from './generated/types.gen';
